import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, api } from "../App";
import { ArrowLeft, BookOpen, Lock, Clock, ChevronRight, Zap, CheckCircle, XCircle, Trophy, Brain } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Progress } from "../components/ui/progress";

function ArticleQuiz({ articleId }) {
  const [quiz, setQuiz] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [previousScore, setPreviousScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState(false);
  const [results, setResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [shuffledOptions, setShuffledOptions] = useState([]);

  useEffect(() => {
    api.get(`/articles/${articleId}/quiz`).then(res => {
      setQuiz(res.data.quiz);
      setCompleted(res.data.completed || false);
      setPreviousScore(res.data.previous_score);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [articleId]);

  useEffect(() => {
    if (quiz && quiz.questions && quiz.questions[currentQ]) {
      const q = quiz.questions[currentQ];
      const opts = [q.correct, ...q.wrong].sort(() => Math.random() - 0.5);
      setShuffledOptions(opts);
    }
  }, [quiz, currentQ]);

  const handleAnswer = (option) => {
    if (revealed) return;
    setAnswers(prev => ({ ...prev, [currentQ]: option }));
    setRevealed(true);
  };

  const nextQuestion = () => {
    setRevealed(false);
    if (currentQ < quiz.questions.length - 1) {
      setCurrentQ(prev => prev + 1);
    } else {
      submitQuiz();
    }
  };

  const submitQuiz = async () => {
    setSubmitting(true);
    const payload = {
      section_id: 0,
      answers: Object.entries(answers).map(([qi, ans]) => ({
        question_index: parseInt(qi),
        answer: ans
      })),
      total_time_spent: 60
    };
    try {
      const res = await api.post(`/articles/${articleId}/quiz/submit`, payload);
      setResults(res.data);
      if (res.data.xp_earned > 0) {
        toast.success(`+${res.data.xp_earned} BTC XP earned!`);
      }
    } catch {
      toast.error("Failed to submit quiz");
    }
    setSubmitting(false);
  };

  if (loading) return null;
  if (!quiz) return null;

  if (results) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card-dark p-6 border border-white/10 mt-6"
        data-testid="quiz-results"
      >
        <div className="text-center">
          <Trophy className={`mx-auto mb-3 ${results.passed ? "text-[#F7931A]" : "text-zinc-500"}`} size={36} />
          <h3 className="font-heading text-xl font-bold text-white mb-2">
            {results.passed ? "Great job!" : "Keep learning!"}
          </h3>
          <div className="flex items-center justify-center gap-6 mb-4">
            <div>
              <p className="font-mono text-2xl text-white">{results.correct}/{results.total}</p>
              <p className="text-zinc-500 text-xs">Correct</p>
            </div>
            <div>
              <p className="font-mono text-2xl text-[#F7931A]">+{results.xp_earned}</p>
              <p className="text-zinc-500 text-xs">BTC XP Earned</p>
            </div>
          </div>
          {results.xp_earned === 0 && (
            <p className="text-zinc-500 text-sm">You already completed this quiz. No additional BTC XP awarded.</p>
          )}
        </div>
      </motion.div>
    );
  }

  if (completed && previousScore) {
    return (
      <div className="card-dark p-5 border border-green-500/20 mt-6" data-testid="quiz-completed">
        <div className="flex items-center gap-3">
          <CheckCircle className="text-green-500 flex-shrink-0" size={24} />
          <div>
            <p className="text-white font-medium text-sm">Quiz completed</p>
            <p className="text-zinc-400 text-xs">Score: {previousScore.correct}/{previousScore.total}</p>
          </div>
        </div>
      </div>
    );
  }

  const q = quiz.questions[currentQ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-6 space-y-4"
      data-testid="article-quiz"
    >
      <div className="card-dark p-5 border border-white/10">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-[#F7931A]/10 flex items-center justify-center">
            <Brain className="text-[#F7931A]" size={18} />
          </div>
          <div>
            <h3 className="text-white font-heading text-sm font-bold">Test Your Knowledge</h3>
            <p className="text-zinc-500 text-xs">5 questions based on this article</p>
          </div>
          <div className="ml-auto flex items-center gap-1 text-[#F7931A] text-xs">
            <Zap size={12} /> 500 BTC XP
          </div>
        </div>

        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-zinc-500 text-xs">Question {currentQ + 1} of {quiz.questions.length}</span>
          </div>
          <Progress value={((currentQ + (revealed ? 1 : 0)) / quiz.questions.length) * 100} className="h-1.5" />
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentQ}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            <p className="text-white text-sm font-medium mb-4" data-testid="quiz-question">{q.question}</p>

            <div className="grid gap-2">
              {shuffledOptions.map((option, idx) => {
                const isSelected = answers[currentQ] === option;
                const isCorrect = revealed && option === q.correct;
                const isWrong = revealed && isSelected && option !== q.correct;

                return (
                  <button
                    key={idx}
                    onClick={() => handleAnswer(option)}
                    disabled={revealed}
                    className={`p-3 rounded-lg border text-left text-sm transition-all duration-300 ${
                      isCorrect
                        ? "border-green-500 bg-green-500/10 text-green-400"
                        : isWrong
                          ? "border-red-500 bg-red-500/10 text-red-400"
                          : isSelected
                            ? "border-[#F7931A] bg-[#F7931A]/10 text-white"
                            : "border-white/5 bg-[#161616] text-zinc-300 hover:border-[#F7931A]/30"
                    }`}
                    data-testid={`quiz-option-${idx}`}
                  >
                    <span className="flex items-center gap-2">
                      {isCorrect && <CheckCircle size={16} className="flex-shrink-0" />}
                      {isWrong && <XCircle size={16} className="flex-shrink-0" />}
                      {option}
                    </span>
                  </button>
                );
              })}
            </div>

            {revealed && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-4 flex justify-end"
              >
                <Button
                  onClick={nextQuestion}
                  disabled={submitting}
                  className="btn-primary text-xs"
                  data-testid="quiz-next-btn"
                >
                  {submitting ? "Submitting..." : currentQ < quiz.questions.length - 1 ? "Next Question" : "See Results"}
                </Button>
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

export default function RabbitHole() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/articles/list").then(res => {
      setArticles(res.data.articles || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const loadArticle = async (id) => {
    try {
      const res = await api.get(`/articles/${id}`);
      setSelected(res.data.article);
    } catch (err) {
      if (err.response?.status === 403) {
        navigate("/pricing");
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="rabbit-hole-page">
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => selected ? setSelected(null) : navigate("/dashboard")} className="text-zinc-400 hover:text-white">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-heading text-xl font-bold text-white">RABBIT <span className="text-[#F7931A]">HOLE</span></h1>
          <BookOpen className="text-[#F7931A]" size={20} />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {!selected ? (
          <div className="space-y-4" data-testid="articles-list">
            {/* Explainer */}
            <div className="card-dark p-5 mb-2 border border-[#F7931A]/20" data-testid="rabbit-hole-explainer">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#F7931A]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <BookOpen className="text-[#F7931A]" size={20} />
                </div>
                <div>
                  <h2 className="text-white font-heading text-base font-bold mb-1">Go Deeper</h2>
                  <p className="text-zinc-400 text-sm leading-relaxed">
                    AI-generated long-form deep dives into specific Bitcoin topics — mining economics, Lightning Network mechanics, 
                    monetary history, and more. Each article is a unique exploration you won't find elsewhere. New articles generated monthly.
                  </p>
                  <div className="flex flex-wrap gap-3 mt-3 text-xs">
                    <span className="flex items-center gap-1 text-[#F7931A]">
                      <Zap size={12} /> 300 BTC XP per read
                    </span>
                    {user?.purchase_tier === "free" && (
                      <span className="flex items-center gap-1 text-yellow-500">
                        <Lock size={12} /> Paid members only
                      </span>
                    )}
                    <span className="flex items-center gap-1 text-zinc-500">
                      <Clock size={12} /> New article every month
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-zinc-400 text-sm">{articles.length} article{articles.length !== 1 ? "s" : ""} available</p>
            </div>

            {articles.length === 0 ? (
              <div className="text-center py-12">
                <BookOpen className="text-zinc-700 mx-auto mb-4" size={48} />
                <p className="text-zinc-500">New articles are published automatically each month. Check back soon!</p>
              </div>
            ) : articles.map((a, i) => (
              <motion.div key={a.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className={`card-dark p-5 cursor-pointer hover:border-[#F7931A]/30 ${a.locked ? "opacity-60" : ""}`}
                onClick={() => a.locked ? navigate("/pricing") : loadArticle(a.id)}
                data-testid={`article-${a.id}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-white font-heading font-bold text-sm">{a.title}</h3>
                    <div className="flex items-center gap-3 mt-2 text-zinc-500 text-xs">
                      <span className="flex items-center gap-1"><Clock size={12} /> {new Date(a.published_at).toLocaleDateString()}</span>
                      <span className="flex items-center gap-1"><Zap size={12} className="text-[#F7931A]" /> 300 BTC XP</span>
                      {a.locked && <span className="text-yellow-500 text-xs">Upgrade to read</span>}
                    </div>
                  </div>
                  {a.locked ? <Lock className="text-zinc-600" size={18} /> : <ChevronRight className="text-zinc-600" size={18} />}
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="article-content">
            <div className="card-dark p-6 border-[#F7931A]/30">
              <h2 className="font-heading text-2xl font-bold text-white mb-2">{selected.title}</h2>
              <p className="text-zinc-500 text-xs">{new Date(selected.published_at).toLocaleDateString()}</p>
            </div>
            <div className="card-dark p-6">
              <div className="text-zinc-300 text-sm leading-relaxed whitespace-pre-line">
                {selected.content}
              </div>
            </div>
            <ArticleQuiz articleId={selected.id} />
          </motion.div>
        )}
      </main>
    </div>
  );
}
